/**
 * Query params the growth loops put on inbound links. `ref` is the referring
 * workspace's portal id and is the only one attribution depends on; `firm` and
 * `first` personalise the signup screen; the UTMs are the tracking contract.
 * `heroArm` is the marketing homepage's hero test arm, which only the signup
 * URL can carry because the site's cookie is not readable from the app.
 *
 * They reach signup on the URL and are read off it at submit, except across the
 * Google Hosted UI hop, which rebuilds the URL from an OAuth custom state built
 * from an explicit allowlist. Anything not named here does not survive that hop.
 */
export const LOOP_ATTRIBUTION_PARAMS = [
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'firm',
  'first',
  'heroArm',
] as const;

const REFERRAL_UTM_MEDIUM = 'referral';

export interface ReferralInvite {
  /** The referrer's first name, when the link carried one. */
  referrerFirstName?: string;
}

/**
 * The referral a signup arrived through, or null for any other visit. Badge
 * links carry a `ref` too, so the referral medium is what marks an invite.
 *
 * Read off the URL rather than the growth-loops flag: signup has no workspace
 * to evaluate the flag against, and a referral link only exists once the
 * referring workspace is on it.
 */
export const getReferralInvite = (params: {
  ref?: string;
  utm_medium?: string;
  first?: string;
}): ReferralInvite | null => {
  if (!params.ref?.trim() || params.utm_medium !== REFERRAL_UTM_MEDIUM) {
    return null;
  }
  return { referrerFirstName: params.first?.trim() || undefined };
};

/**
 * Picks the attribution params out of a query string, dropping blanks. Accepts
 * a missing search because callers read it off `window.location`, which tests
 * routinely stub without one — and no search simply means nothing to carry.
 */
export const readLoopAttributionParams = (
  search: string | URLSearchParams | null | undefined,
): Record<string, string> => {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(search ?? '');
  const attribution: Record<string, string> = {};
  LOOP_ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) {
      attribution[key] = value;
    }
  });
  return attribution;
};

/**
 * Custom state is a path plus a query string (`signup?referrer=studio&ref=…`),
 * so the path has to come off before the query can be parsed.
 */
export const readLoopAttributionFromCustomState = (
  customState: string | null | undefined,
): Record<string, string> => {
  if (!customState) {
    return {};
  }
  const queryStart = customState.indexOf('?');
  return readLoopAttributionParams(
    queryStart >= 0 ? customState.slice(queryStart + 1) : customState,
  );
};

/**
 * Attribution to carry across the Google hop, read from the URL the user
 * clicked Continue with Google on.
 *
 * Deliberately not gated on growth loops. This only ever runs on `/signup` and
 * `/login`, where no workspace exists yet and the flag is evaluated against a
 * placeholder hostname, so any flag carrying release conditions reads false
 * there for everyone — a targeted rollout would silently carry no attribution
 * and every Google signup would look organic. Carrying params that nothing
 * reads until the flag is on changes no behaviour, which is the same reason the
 * submit-time capture this feeds ships unflagged.
 */
export const getLoopAttributionForCustomState = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }
  return readLoopAttributionParams(window.location.search);
};

/**
 * Merges the attribution on the current URL into params a navigation is about
 * to rebuild. Explicit params win.
 *
 * Auth screens assemble their URLs from an allowlist of known fields, so
 * anything not named is shed on every hop. Without this, a visitor who lands
 * on login and follows the switch link to signup arrives with no attribution
 * at all, and the signup counts as organic.
 *
 * Deliberately not gated on growth loops: the submit-time capture this feeds
 * ships unflagged, so gating here would leave that capture broken for exactly
 * the routes this exists to fix. Carrying a param that is already on the URL
 * changes nothing on its own.
 */
export const withLoopAttribution = (
  params: Record<string, string>,
): Record<string, string> => {
  if (typeof window === 'undefined') {
    return params;
  }
  return { ...readLoopAttributionParams(window.location.search), ...params };
};

/**
 * Puts decoded attribution back on the current URL without reloading. Values
 * already on the URL win, so this can never overwrite a live link's params.
 */
export const restoreLoopAttributionToUrl = (
  attribution: Record<string, string>,
) => {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  const missing = Object.entries(attribution).filter(
    ([key]) => !url.searchParams.has(key),
  );
  if (missing.length === 0) {
    return;
  }
  missing.forEach(([key, value]) => url.searchParams.set(key, value));
  window.history.replaceState({}, '', url);
};
